import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import * as braze from "@braze/web-sdk";
import { datadogRum } from "@datadog/browser-rum";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useGetSurveySessionRealTime } from "../hooks/useGetSurveySessionRealTime";
import { useGetMsoData } from "../hooks/useMsoData";
import { CheckoutRoutes, SmsSmartRecoveryRoutes } from "../typings/enums";
import { isBrazeSyncableMso } from "../utils/brazePreferredMedication";

const { VITE_BRAZE_API_KEY, VITE_BRAZE_BASE_URL } = import.meta.env;

// ---------------------------------------------------------------------------
// In-app message route scoping
// ---------------------------------------------------------------------------
//
// In-app messages should surface ONLY on the authenticated dashboard / member
// experience (the PrivateRoute-gated app: /tabs, /treatment-plan, /messages,
// etc.), NOT on the funnel / checkout / quiz / results / account-recovery
// routes. We can't scope Braze's global automaticallyShowInAppMessages() per
// route, so instead we subscribe and only call showInAppMessage() when the
// CURRENT route is a dashboard route.
//
// "Dashboard route" is defined as the complement of the funnel/checkout set
// below, so newly added member routes are covered by default.
const FUNNEL_CHECKOUT_ROUTE_PREFIXES: readonly string[] = [
  CheckoutRoutes.Approval, // /approval
  CheckoutRoutes.Results, // /results
  CheckoutRoutes.Address, // /address
  CheckoutRoutes.Payment, // /payment
  CheckoutRoutes.AffirmPayment, // /affirm-payment
  // The screen is gone, but payments still returns here after a transaction
  // until FLEX-361 repoints `return_url`, so the path stays excluded.
  CheckoutRoutes.Account, // /account
  SmsSmartRecoveryRoutes.Offer, // /offer/rt-sms-sr
  "/survey", // quiz
  "/results", // results router (defensive; CheckoutRoutes.Results is also /results)
  "/disqualified",
  "/create-account",
  "/activate-notifications",
  "/login",
];

export const isDashboardRoute = (pathname: string): boolean =>
  !FUNNEL_CHECKOUT_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PurchaseEvent {
  productId: string;
  price: number;
  currency: string;
  quantity: number;
  properties: object;
}

export interface BrazeContextValue {
  isReady: boolean;
  logPurchase: (
    productId: string,
    price: number,
    purchaseProperties?: object,
    currencyCode?: string,
    quantity?: number
  ) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isValidProvider = isBrazeSyncableMso;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const BrazeContext = createContext<BrazeContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const BrazeProvider = ({ children }: PropsWithChildren) => {
  // ---- Auth ----
  const { currentUser } = useAuth();
  const location = useLocation();

  // ---- Data dependencies (called ONCE here, not per consumer) ----
  const { surveySession, hasSurveySessionId } = useGetSurveySessionRealTime();
  const { medicalProvider, isLoading: isMsoLoading } = useGetMsoData();

  // ---- Derived ----
  // Use the survey session's MSO as the primary source for the exclusion check.
  // useGetMsoData relies on a userId-based query that resolves later than the
  // URL-based surveySession, causing a race where Braze initializes before
  // the exclusion gate can evaluate.
  const surveyMso = surveySession?.user?.medicalProvider;
  const effectiveProvider = medicalProvider || surveyMso;

  // MSO is resolved when:
  // 1. We have a provider value (steadymd, openloop, etc.), OR
  // 2. There's no survey session to load it from (direct login flow), OR
  // 3. The survey session loaded but has no medicalProvider (e.g. nutrition users)
  //    — we know the data arrived, and the user isn't OL, so it's safe to proceed.
  const isMsoResolved =
    !!effectiveProvider || !hasSurveySessionId || !!surveySession;

  // `PostPurchaseReturn` (the surviving handler on /account, FLEX-360) signs
  // the user in asynchronously via `firebase_token` before navigating away.
  // `currentUser` only updates once that resolves, so without this gate the
  // identity effect below can fire on whatever stale (or absent) session was
  // active before the exchange, changeUser()-ing and flushing buffered
  // purchases under the wrong account. It clears itself once the exchange
  // finishes and the route navigates elsewhere.
  const isAccountReturnAuthPending =
    location.pathname === CheckoutRoutes.Account &&
    new URLSearchParams(location.search).has("firebase_token");

  // ---- Reactive state ----
  const [isReady, setIsReady] = useState(false);

  // ---- Refs (stable, no re-renders) ----
  const initializedRef = useRef(false);
  // Tracks the real, authenticated account uid we've identified to Braze so the
  // identity effect only calls changeUser once per real account.
  const identifiedUserIdRef = useRef<string | undefined>();
  // Tracks the last email pushed to Braze (keyed implicitly by the identified
  // uid, which is reset below on changeUser). Firebase can surface uid BEFORE
  // email, so setEmail must NOT be gated behind the once-per-uid changeUser
  // guard — otherwise a later render that finally has the email would skip it.
  const lastEmailSetRef = useRef<string | undefined>();
  // Latest pathname, read by the in-app-message subscription callback so it
  // always evaluates the CURRENT route (the subscription is registered once).
  const currentPathRef = useRef(location.pathname);
  currentPathRef.current = location.pathname;

  // ---- Data buffers ----
  // Only purchases are buffered before init now. Profile / attribute / SMS
  // writes are owned by the backend (FLEX-150) and no longer touch the SDK, so
  // there is nothing to buffer for them.
  const pendingPurchasesRef = useRef<PurchaseEvent[]>([]);

  // ---- Helpers ----
  const flushBuffers = useCallback(() => {
    const failedPurchases: PurchaseEvent[] = [];
    let purchaseCount = 0;
    pendingPurchasesRef.current.forEach((e) => {
      try {
        braze.logPurchase(
          e.productId,
          e.price,
          e.currency,
          e.quantity,
          e.properties
        );
        purchaseCount += 1;
      } catch (error) {
        failedPurchases.push(e);
        datadogRum.addError(
          error instanceof Error ? error : new Error(String(error)),
          {
            context: "braze.buffered_purchase_failed",
            productId: e.productId,
          }
        );
      }
    });
    pendingPurchasesRef.current = failedPurchases;

    if (purchaseCount > 0) {
      datadogRum.addAction("braze.buffers_flushed", { purchaseCount });
      return true;
    }
    return false;
  }, []);

  // Push the account's email to Braze if we have one we haven't set yet. This is
  // deliberately decoupled from the once-per-uid changeUser guard: Firebase can
  // provide currentUser.uid before currentUser.email, so the email may only
  // become available on a later render after the uid was already identified.
  // Returns true if setEmail was called.
  const pushEmailIfNeeded = useCallback((email?: string | null) => {
    if (!email) return false;
    if (email === lastEmailSetRef.current) return false;
    braze.getUser()?.setEmail(email);
    lastEmailSetRef.current = email;
    return true;
  }, []);

  // Initialize the Braze SDK for a REAL, authenticated account only.
  //
  // Init runs in Braze's recommended order: initialize() → changeUser(uid) →
  // openSession(). We NEVER open an anonymous session: anonymous funnel visitors
  // get no Braze session at all, which eliminates the email-less anonymous
  // device profiles (a primary FLEX-150 goal). changeUser(uid) keys the session
  // to the real account's external_id (same key the backend's lead-identity-api
  // uses), so the account already carries its native identity; setEmail adds the
  // native email when available.
  //
  // In-app messages are scoped to dashboard routes via subscribeToInAppMessage
  // (see isDashboardRoute) rather than the global automaticallyShowInAppMessages,
  // which cannot be scoped per route.
  const ensureBrazeInitialized = useCallback(
    ({
      userId: realUserId,
      email,
      flushPendingBuffers = true,
      context,
    }: {
      userId: string;
      email?: string | null;
      flushPendingBuffers?: boolean;
      context: string;
    }) => {
      if (!VITE_BRAZE_API_KEY || !VITE_BRAZE_BASE_URL) {
        datadogRum.addError(new Error("braze.missing_config"), {
          missing: !VITE_BRAZE_API_KEY ? "API_KEY" : "BASE_URL",
          context,
        });
        return false;
      }

      if (initializedRef.current) {
        return true;
      }

      try {
        braze.initialize(VITE_BRAZE_API_KEY, {
          baseUrl: VITE_BRAZE_BASE_URL,
          enableLogging: false,
        });

        // Show in-app messages only on dashboard/member routes, evaluated
        // against the CURRENT route at message time (not registration time).
        braze.subscribeToInAppMessage((message) => {
          if (isDashboardRoute(currentPathRef.current)) {
            braze.showInAppMessage(message);
          }
        });

        braze.changeUser(realUserId);
        braze.openSession();
        identifiedUserIdRef.current = realUserId;
        // New user identity: forget any email previously pushed for a prior uid.
        lastEmailSetRef.current = undefined;

        pushEmailIfNeeded(email);
        braze.requestImmediateDataFlush();

        initializedRef.current = true;

        if (flushPendingBuffers) {
          flushBuffers();
        }
        setIsReady(true);
        return true;
      } catch (error) {
        datadogRum.addError(
          error instanceof Error ? error : new Error(String(error)),
          { context }
        );
        return false;
      }
    },
    [flushBuffers, pushEmailIfNeeded]
  );

  // ---- Effect: Initialize + identify — authenticated accounts ONLY ----
  // Braze is initialized ONLY for a known real account (not anonymous, has a
  // uid). Anonymous funnel visitors get NO Braze session at all — no
  // openSession, no in-app messaging, no email-less device profile (a primary
  // FLEX-150 goal). There is no approval-route init delay.
  //
  // After init: changeUser fires once per uid (guarded by identifiedUserIdRef),
  // but setEmail is handled separately by pushEmailIfNeeded so a later render
  // that finally carries the email still pushes it (Firebase can surface uid
  // before email).
  useEffect(() => {
    if (isMsoLoading || !isMsoResolved || isAccountReturnAuthPending) return;
    if (currentUser?.isAnonymous) return;

    const realUserId = currentUser?.uid;
    if (!realUserId) return;

    if (!initializedRef.current) {
      ensureBrazeInitialized({
        userId: realUserId,
        email: currentUser?.email,
        context: "braze.init_failed",
      });
      return;
    }

    // Re-key the session only when the real uid changes (account switch, or
    // anonymous → authenticated upgrade).
    let didWrite = false;
    if (realUserId !== identifiedUserIdRef.current) {
      braze.changeUser(realUserId);
      identifiedUserIdRef.current = realUserId;
      lastEmailSetRef.current = undefined;
      didWrite = true;
    }

    // Push the email whenever it's newly available for the identified account —
    // independent of the changeUser guard above.
    if (pushEmailIfNeeded(currentUser?.email)) {
      didWrite = true;
    }

    if (didWrite) {
      braze.requestImmediateDataFlush();
    }
  }, [
    isMsoLoading,
    isMsoResolved,
    isAccountReturnAuthPending,
    currentUser?.uid,
    currentUser?.isAnonymous,
    currentUser?.email,
    ensureBrazeInitialized,
    pushEmailIfNeeded,
  ]);

  // ---- Monitoring: declarative blockers ----
  const waitingFor = useMemo(() => {
    const blockers: string[] = [];
    if (isMsoLoading) blockers.push("msoData");
    if (hasSurveySessionId && !surveySession) blockers.push("surveySession");
    if (!isReady) blockers.push("brazeInit");
    if (isAccountReturnAuthPending) blockers.push("authRecovery");
    return blockers;
  }, [
    isMsoLoading,
    hasSurveySessionId,
    surveySession,
    isReady,
    isAccountReturnAuthPending,
  ]);

  const waitingForKey = waitingFor.join(",");
  useEffect(() => {
    if (waitingFor.length > 0) {
      datadogRum.addAction("braze.waiting_for_deps", {
        blockers: waitingFor,
      });
    } else {
      datadogRum.addAction("braze.all_deps_resolved");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingForKey]);

  // ---- Consumer functions ----

  const logPurchase = useCallback(
    (
      productId: string,
      price: number,
      purchaseProperties: object = {},
      currencyCode: string = "USD",
      quantity: number = 1
    ) => {
      if (!isValidProvider(effectiveProvider)) return;
      if (initializedRef.current) {
        braze.logPurchase(
          productId,
          price,
          currencyCode,
          quantity,
          purchaseProperties
        );
      } else {
        pendingPurchasesRef.current.push({
          productId,
          price,
          currency: currencyCode,
          quantity,
          properties: purchaseProperties,
        });
      }
    },
    [effectiveProvider]
  );

  // ---- Context value (memoized) ----
  const ctx = useMemo<BrazeContextValue>(
    () => ({
      isReady,
      logPurchase,
    }),
    [isReady, logPurchase]
  );

  return <BrazeContext.Provider value={ctx}>{children}</BrazeContext.Provider>;
};

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

export const useBraze = (): BrazeContextValue => {
  const ctx = useContext(BrazeContext);
  if (!ctx) {
    throw new Error("useBraze must be used within <BrazeProvider>");
  }
  return ctx;
};
